import React from 'react';
import { Box, Container, Typography, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { images, getImageUrl } from '../utils/imageUtils';

const AboutSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  background: theme.palette.background.default,
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
}));

const AnimatedCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 3),
  borderRadius: 18,
  background: 'linear-gradient(135deg, #181818 80%, #223a5e 100%)', // use a standard blue
  border: '2px solid #c9a74c',
  boxShadow: '0 8px 32px rgba(201,167,76,0.10)',
  color: theme.palette.text.primary,
  transition: 'transform 0.3s, box-shadow 0.3s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.04)',
    boxShadow: '0 16px 48px rgba(201,167,76,0.18)',
    borderColor: '#ffd700',
  },
}));

const IconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  fontSize: 72,
  color: '#c9a74c',
  filter: 'drop-shadow(0 2px 8px #223a5e66)', // use a standard blue
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5, 4),
  borderRadius: 24,
  background: 'rgba(24,26,32,0.65)', // more transparent
  border: '2.5px solid transparent', // transparent by default
  boxShadow: '0 8px 32px 0 rgba(201,167,76,0.18)',
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(12px)', // more blur for glass effect
  transition: 'transform 0.4s cubic-bezier(.4,2,.6,1), box-shadow 0.4s cubic-bezier(.4,2,.6,1), border-color 0.4s cubic-bezier(.4,2,.6,1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.03) translateY(-6px)',
    boxShadow: '0 16px 48px 0 #c9a74c55',
    borderColor: '#ffd700', // gold border on hover
  },
  animation: 'fadeInUp 1s cubic-bezier(.4,2,.6,1)',
}));

const VisionMissionCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(24,26,32,0.92)',
  border: '2px solid #c9a74c',
  borderRadius: 18,
  boxShadow: '0 2px 12px #c9a74c22',
  color: '#fff',
  padding: theme.spacing(3, 2),
  textAlign: 'center',
  margin: '0 12px',
  minWidth: 220,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const About = () => {
  const { t } = useTranslation();

  return (
    <AboutSection id="about">
      <Container maxWidth="md">
        <GlassCard elevation={8}>
          <IconWrapper style={{ marginBottom: 0, marginTop: -48 }}>
            <InfoOutlinedIcon fontSize="inherit" />
          </IconWrapper>
          <Typography variant="h2" align="center" gutterBottom sx={{ color: '#fff', fontWeight: 900, letterSpacing: '-1px', mb: 2 }}>
            {t('about.title')}
          </Typography>
          <Typography paragraph align="center" sx={{ fontSize: '1.18rem', color: '#c9a74c', mb: 3, maxWidth: 600, mx: 'auto' }}>
            We deliver reliable and efficient marine cargo transport with integrity and trust.<br />
            Our vision is to be recognized for integrity and reliability in marine cargo transport.
          </Typography>
          <Typography variant="h4" align="center" sx={{ color: '#c9a74c', fontWeight: 800, mb: 2 }}>
            Why Choose Us?
          </Typography>
          <Typography align="center" sx={{ color: '#fff', fontSize: '1.13rem', mb: 3 }}>
            • Integrity and reliability at every step<br />
            • Modern marine cargo solutions<br />
            • Customer-focused, efficient, and secure
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 3 }}>
            <VisionMissionCard elevation={4}>
              <EmojiEventsIcon sx={{ fontSize: 40, color: '#c9a74c', mb: 1 }} />
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                Our Mission
              </Typography>
              <Typography sx={{ color: '#c9a74c', fontSize: '1.05rem' }}>
                Deliver reliable, efficient marine cargo transport with integrity and trust.
              </Typography>
            </VisionMissionCard>
            <VisionMissionCard elevation={4}>
              <VisibilityIcon sx={{ fontSize: 40, color: '#c9a74c', mb: 1 }} />
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                Our Vision
              </Typography>
              <Typography sx={{ color: '#c9a74c', fontSize: '1.05rem' }}>
                Be recognized for integrity and reliability in marine cargo transport.
              </Typography>
            </VisionMissionCard>
          </Box>
        </GlassCard>
      </Container>
    </AboutSection>
  );
};

export default About; 